import { clientFetch } from '~/lib/.client/fetch';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import { connectToSupabase } from '~/lib/.client/supabase';
import { applicationManager } from '~/lib/stores/applicationManager';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore } from '~/lib/stores/supabase';
import type { DeployEdgeFunctionPayload, GetEdgeFunctionsBody } from '~/routes/api.supabase.functions.$projectId';
import type { GetAllMigrationsBody } from '~/routes/api.supabase.migrations.$projectId';
import {
  SUPABASE_ERROR_CODES,
  type SupabaseIntegration,
  type SupabaseOrganizationInfo,
  type SupabaseProjectStatus,
  type SupabaseRegion,
} from '~/types/supabase';
import type { SupabaseOrganizationWithProjects } from '~/types/supabase';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('SupabaseAPI');
const requestWithSupabase = requestWithAuth(globalThis.origin, false);

export async function callSupabaseConnect({
  forceCreation = false,
  supabaseProjectData,
  pollProjectStatus,
  supabaseProjectRemoved,
  projectLimitReached,
  projectNameAlreadyExists,
  needsExtraConfirmation,
  supabaseAuthenticationNeeded,
}: {
  forceCreation: boolean;
  supabaseProjectData?: { supabaseProjectId: string } | { name: string; password: string; region: SupabaseRegion };
  pollProjectStatus: () => void;
  supabaseProjectRemoved: () => void;
  projectLimitReached: () => void;
  projectNameAlreadyExists: () => void;
  needsExtraConfirmation: () => void;
  supabaseAuthenticationNeeded: () => void;
}): Promise<undefined | SupabaseIntegration> {
  const projectName =
    supabaseProjectData && 'name' in supabaseProjectData && supabaseProjectData.name
      ? supabaseProjectData.name
      : projectStore.getSlug();

  const response = await requestWithSupabase<SupabaseIntegration>(
    `api/supabase/connect?forceCreation=${forceCreation}`,
    {
      projectId: projectStore.getProjectId(),
      ...supabaseProjectData,
      projectName,
    },
    'POST',
    {
      202: () => {
        pollProjectStatus();

        return true;
      },
      410: () => {
        supabaseProjectRemoved();

        return true;
      },
      403: () => {
        supabaseAuthenticationNeeded();

        return true;
      },
      400: async (response) => {
        const text = await response.text();

        if (text === SUPABASE_ERROR_CODES.PROJECT_LIMIT_REACHED) {
          projectLimitReached();

          return true;
        }

        if (text === SUPABASE_ERROR_CODES.PROJECT_NAME_ALREADY_EXISTS) {
          projectNameAlreadyExists();

          return true;
        }

        return false;
      },
      409: async (response) => {
        const text = await response.text();

        if (text === SUPABASE_ERROR_CODES.NEED_EXTRA_CONFIRMATION_TO_CREATE_DATABASE) {
          needsExtraConfirmation();

          return true;
        }

        return false;
      },
    },
  );

  return response;
}

export async function fetchSupabaseIntegration(
  projectId: string,
): Promise<undefined | SupabaseIntegration | { notFound: true }> {
  let notFound = false;

  const response = await requestWithSupabase<SupabaseIntegration>(
    `api/project/integrations/supabase/${projectId}`,
    undefined,
    'GET',
    {
      202: () => {
        // we call this function as a fire-and-forget, it handles states update separately
        connectToSupabase(false, false);

        return true;
      },
      403: async () => {
        await applicationManager.disconnect('SUPABASE');

        supabaseStore.supabaseStatus.set('NOT_CONFIGURED');

        return true;
      },
      410: () => {
        supabaseStore.supabaseStatus.set('REMOVED');

        return true;
      },
      404: () => {
        notFound = true;

        return true;
      },
    },
  );

  if (notFound) {
    return { notFound };
  }

  return response;
}

export async function deleteSupabaseProjectIntegration(projectId: string) {
  const response = await requestWithSupabase<{ apiKey: string; supabaseProjectId: string }>(
    `api/project/integrations/supabase/${projectId}`,
    undefined,
    'DELETE',
  );

  return response;
}

export async function pauseProject(projectId: string) {
  const response = await requestWithSupabase<{ success: boolean }>(
    `api/supabase/projects/${projectId}/pause`,
    undefined,
    'POST',
  );

  return response;
}

export async function restoreProject(projectId: string, projectLimitReached: VoidFunction) {
  const response = await requestWithSupabase<{ success: boolean }>(
    `api/supabase/projects/${projectId}/restore`,
    undefined,
    'POST',
    {
      400: async (response) => {
        const text = await response.text();

        if (text === SUPABASE_ERROR_CODES.PROJECT_LIMIT_REACHED) {
          projectLimitReached();

          return true;
        }

        return false;
      },
    },
  );

  return response;
}

export async function getOrganizationInfo(organizationId: string) {
  const response = await requestWithSupabase<{ organization: SupabaseOrganizationInfo }>(
    `api/supabase/organizations/${organizationId}`,
    undefined,
    'GET',
  );

  return response;
}

export async function getProjectStatus(projectId: string) {
  const response = await requestWithSupabase<SupabaseProjectStatus>(
    `api/supabase/projects/${projectId}/status`,
    undefined,
    'GET',
  );

  return response;
}

export async function getAllMigrations(projectId: string, signal?: AbortSignal) {
  const response = await clientFetch(`/api/supabase/migrations/${projectId}`, { signal });

  const result = await response.json<GetAllMigrationsBody>();

  if (!result || !result.success) {
    throw new Error(result?.error ?? 'An unknown error occurred.');
  }

  return result.migrations;
}

export async function applyMigration(projectId: string, filename: string, content: string) {
  const response = await clientFetch(`/api/supabase/migrations/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({
      filename,
      content,
    }),
  });

  const result = await response.json<{ success: true } | { success: false; error: string }>();

  if (!result.success) {
    throw new Error(result.error);
  }
}

export async function listEdgeFunctions(projectId: string, signal?: AbortSignal) {
  const url = `/api/supabase/functions/${projectId}`;

  const response = await clientFetch(url, {
    signal,
    method: 'GET',
  });

  const result = await response.json<GetEdgeFunctionsBody>();

  // soft ignore errors because they might be acceptable
  if (!result || !result.success) {
    logger.error(`Error invoking ${url}:`, result?.error ?? 'An unknown error occurred.');

    return [];
  }

  return result.data;
}

export async function deployEdgeFunction(projectId: string, data: DeployEdgeFunctionPayload): Promise<string> {
  const response = await clientFetch(`/api/supabase/functions/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  const result = await response.json<{ success: true; id: string } | { success: false; error: string }>();

  if (!result.success) {
    throw new Error(result.error);
  }

  return result.id;
}

export async function getEdgeFunctionLogs(projectId: string, functionId: string) {
  const response = await clientFetch(`/api/supabase/functions/${projectId}/logs/${functionId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Unknown error');
  }

  const result = await response.json<{ success: true; logs: string[] } | { success: false; error: string }>();

  if (result.success) {
    return result.logs;
  }

  throw new Error(result.error);
}

export async function getOrganizations() {
  const response = await clientFetch('/api/supabase/organizations');

  if (!response.ok && response.status === 403) {
    throw new Error('FORBIDDEN');
  }

  const result = await response.json<{ organizations: SupabaseOrganizationWithProjects[] }>();

  return result;
}
